/**
 * Renewal queue membership, shared by the Renewals page and the left-nav
 * count (ADO #1597) so the badge and the table cannot disagree.
 *
 * A group is in the queue when its current in-force plan year (or, past due,
 * its most recently ended year) has an end date and no successor year exists.
 * A group drops off the moment someone creates its next plan year.
 */

// Direct module paths, not the '@entities/ops-group' barrel: the barrel pulls in
// the Dataverse client and its env config, which a unit test cannot load.
import type { OpsGroup } from '@entities/ops-group/model/types';
import type { ContractYear } from '@entities/contract-year';
import { groupLifecycle } from '@entities/ops-group/lib/lifecycle';
// Direct module path for the same reason: renewalQueue.test.ts mocks the barrel.
import { todayLocalIso } from '@shared/lib/fmt';

export interface RenewalQueueEntry {
  group: OpsGroup;
  queueYear: ContractYear;
  daysUntilEnd: number;
}

/** Local calendar day: the UTC day flips at 7pm Central and moved countdowns a day early. */
export function todayStr(): string {
  return todayLocalIso();
}

export function daysBetween(from: string, to: string): number {
  const a = new Date(`${from.split('T')[0]}T00:00:00`);
  const b = new Date(`${to.split('T')[0]}T00:00:00`);
  return Math.round((b.getTime() - a.getTime()) / (24 * 60 * 60 * 1000));
}

export function buildRenewalQueue(
  groups: OpsGroup[],
  allYears: ContractYear[],
  today: string = todayStr(),
): RenewalQueueEntry[] {
  const yearsByAccount = new Map<string, ContractYear[]>();
  for (const cy of allYears) {
    const list = yearsByAccount.get(cy.accountId) ?? [];
    list.push(cy);
    yearsByAccount.set(cy.accountId, list);
  }

  const result: RenewalQueueEntry[] = [];
  for (const group of groups) {
    const years = yearsByAccount.get(group.accountId) ?? [];
    if (years.length === 0) continue;

    // Current in-force year; when there is none, fall back to the most
    // recently ENDED year so past-due groups (year ended, nobody renewed)
    // surface in the queue instead of silently vanishing.
    const inForce = years.find((y) => {
      if (!y.startDate || !y.endDate) return false;
      const s = y.startDate.split('T')[0]!;
      const e = y.endDate.split('T')[0]!;
      return s <= today && today <= e;
    });
    const queueYear = inForce?.endDate
      ? inForce
      : years
          .filter((y) => y.startDate && y.endDate && y.endDate.split('T')[0]! < today)
          .sort((a, b) => b.endDate!.localeCompare(a.endDate!))[0];
    if (!queueYear?.endDate) continue;

    // A successor year (any year starting after this one ends) means someone
    // already renewed, so the group leaves the queue.
    const endStr = queueYear.endDate.split('T')[0]!;
    const hasSuccessor = years.some((y) => {
      if (y.id === queueYear.id || !y.startDate) return false;
      return y.startDate.split('T')[0]! > endStr;
    });
    if (hasSuccessor) continue;

    const daysUntilEnd = daysBetween(today, endStr);
    if (!Number.isFinite(daysUntilEnd)) continue; // corrupted date strings

    result.push({ group, queueYear, daysUntilEnd });
  }
  return result;
}

/**
 * Nav-badge count: queue entries excluding off-book (termed or archived)
 * groups, which the page hides behind its Show-termed switch, and limited to
 * the Past due and 0-30 buckets (Sasha 9/01, refining ADO #1597): the badge
 * is a call to action, and 400+ renewals due someday drowned out the 36 due
 * now. The 30-day line matches the page's own bucket edge in getDaysBucket.
 */
export function countRenewalQueue(groups: OpsGroup[], allYears: ContractYear[]): number {
  return buildRenewalQueue(groups, allYears)
    .filter((e) => !groupLifecycle(e.group).offBook && e.daysUntilEnd <= 30).length;
}

/** Days-until-end buckets. The Renewals page's bucket bar and every countdown share these edges. */
export type RenewalDaysBucket = 'past' | '0-30' | '31-60' | '61-90' | '91-120' | '121+';

export function getDaysBucket(days: number): RenewalDaysBucket {
  if (days < 0) return 'past';
  if (days <= 30) return '0-30';
  if (days <= 60) return '31-60';
  if (days <= 90) return '61-90';
  if (days <= 120) return '91-120';
  return '121+';
}

/**
 * How loudly to show a countdown: 30 days or less (past due included) is
 * urgent, 60 or less is a warning, anything further is quiet. The Renewals
 * page's day coloring draws the same two lines.
 */
export type RenewalUrgency = 'urgent' | 'warning' | 'quiet';

export function renewalUrgency(days: number): RenewalUrgency {
  if (days <= 30) return 'urgent';
  if (days <= 60) return 'warning';
  return 'quiet';
}

/** A countdown only makes sense for a group staying on the book. */
function isCountingDown(group: OpsGroup): boolean {
  const lc = groupLifecycle(group);
  return !lc.archived && lc.status === 'active';
}

/**
 * One group's renewal countdown (ADO #1632): days until its current plan year
 * ends, on exactly the Renewals queue's terms, so the group page chip, the My
 * Groups column and the Renewals page always show the same number. Null when
 * there is nothing to count down to: the group is out of the queue (its next
 * plan year already exists, or it has no dated year), or it is leaving the
 * book (terming, termed, or archived), where a countdown beside the term badge
 * would contradict it.
 */
export function renewalCountdown(
  group: OpsGroup,
  years: ContractYear[],
  today: string = todayStr(),
): RenewalQueueEntry | null {
  if (!isCountingDown(group)) return null;
  return buildRenewalQueue([group], years, today)[0] ?? null;
}

/** renewalCountdown for a whole book: daysUntilEnd keyed by accountId. */
export function renewalCountdownsByAccount(
  groups: OpsGroup[],
  allYears: ContractYear[],
  today: string = todayStr(),
): Map<string, number> {
  const map = new Map<string, number>();
  for (const entry of buildRenewalQueue(groups, allYears, today)) {
    if (isCountingDown(entry.group)) map.set(entry.group.accountId, entry.daysUntilEnd);
  }
  return map;
}

/** Header chip wording. The count runs to the day the current plan year ends. */
export function renewalCountdownLabel(days: number): string {
  const n = Math.abs(days);
  const unit = n === 1 ? 'day' : 'days';
  if (days < 0) return `Renewal ${n} ${unit} past due`;
  if (days === 0) return 'Plan year ends today';
  return `Renewal in ${n} ${unit}`;
}
